import { type FormEvent, useEffect, useState } from "react";

import { PRODUCT_NAME } from "@src/shared/brand";
import {
  Wizard,
  WizardPanel,
  WizardProgress,
} from "@src/shared/components/v2/Wizard";

import { type SignUpStepId } from "../../../../domain";
import {
  type SignUpAccountValues,
  type SignUpProvisioningView,
} from "../../../../domain/v2";
import { AccountProfileStep } from "../../../components/AccountProfileStep";
import { EmailVerificationStep } from "../../../components/EmailVerificationStep";
import { InvitationJoinStep } from "../../../components/InvitationJoinStep";
import { LoopPreviewStep } from "../../../components/LoopPreviewStep";
import { MicrophoneStep } from "../../../components/MicrophoneStep";
import { ProvisioningGateStep } from "../../../components/ProvisioningGateStep";
import { SignUpStepFooter } from "../../../components/SignUpStepFooter";
import { SignUpStepLayout } from "../../../components/SignUpStepLayout";

export interface SignUpInvitation {
  email: string;
  firstName: string;
  lastName: string;
  practiceName: string;
}

export interface SignUpPageV2Props {
  activeStep: SignUpStepId;
  accountError?: string | null;
  emailVerificationError?: string | null;
  verificationEmail?: string;
  initialValues?: Partial<SignUpAccountValues>;
  /**
   * Set when the wizard was opened from an invitation email. It swaps the final
   * step from email verification to a join confirmation and pins the account to
   * the invited address.
   */
  invitation?: SignUpInvitation;
  invitationAcceptError?: string | null;
  invitationError?: string | null;
  isAcceptingInvitation?: boolean;
  isAccountSubmitting?: boolean;
  isEmailVerificationChecking?: boolean;
  isEmailVerificationPending?: boolean;
  isInvitation?: boolean;
  isLoadingInvitation?: boolean;
  isVerificationEmailResending?: boolean;
  provisioningView?: SignUpProvisioningView;
  onCreateAccount?: (
    values: SignUpAccountValues,
  ) => boolean | void | Promise<boolean | void>;
  onCheckEmailVerification?: () => void;
  onFinishInvitation?: () => void;
  onResendVerificationEmail?: () => void;
  onStepChange?: (step: SignUpStepId) => void;
  onComplete?: () => void;
  onSkip?: () => void;
}

const DEFAULT_VALUES: SignUpAccountValues = {
  email: "",
  password: "",
  firstName: "",
  lastName: "",
};

function getInitialValues(
  initialValues: Partial<SignUpAccountValues> | undefined,
): SignUpAccountValues {
  return {
    ...DEFAULT_VALUES,
    ...initialValues,
  };
}

function canCreateAccount(values: SignUpAccountValues) {
  return (
    values.email.trim().length > 0 &&
    values.password.length >= 10 &&
    values.firstName.trim().length > 0 &&
    values.lastName.trim().length > 0
  );
}

function getPreviousStep(step: SignUpStepId): SignUpStepId | null {
  switch (step) {
    case "microphone":
      return "profile";
    case "preview":
      return "microphone";
    case "trust":
      return "preview";
    case "verify-email":
      return "trust";
    case "profile":
      return null;
  }
}

export function SignUpPageV2({
  activeStep,
  accountError = null,
  emailVerificationError = null,
  verificationEmail,
  initialValues,
  invitation,
  invitationAcceptError = null,
  invitationError = null,
  isAcceptingInvitation = false,
  isAccountSubmitting = false,
  isEmailVerificationChecking = false,
  isEmailVerificationPending = false,
  isInvitation = false,
  isLoadingInvitation = false,
  isVerificationEmailResending = false,
  provisioningView = { state: "idle" },
  onCreateAccount,
  onCheckEmailVerification,
  onFinishInvitation,
  onResendVerificationEmail,
  onStepChange,
  onComplete,
  onSkip,
}: SignUpPageV2Props) {
  const [values, setValues] = useState(() => getInitialValues(initialValues));
  const [isMicrophoneReady, setIsMicrophoneReady] = useState(false);
  const canSubmitAccount = canCreateAccount(values);
  const hasSubmittedAccount =
    isEmailVerificationPending || provisioningView.state !== "idle";
  const displayedVerificationEmail =
    invitation?.email || verificationEmail || values.email;

  // The invitation only resolves after the first render, and it owns the
  // identity fields, so adopt it as soon as it lands.
  useEffect(() => {
    if (!invitation) return;

    setValues((current) => ({
      ...current,
      email: invitation.email,
      firstName: current.firstName || invitation.firstName,
      lastName: current.lastName || invitation.lastName,
    }));
  }, [invitation]);

  const goToStep = (step: SignUpStepId) => {
    onStepChange?.(step);
  };

  const goBack = () => {
    const previousStep = getPreviousStep(activeStep);
    if (previousStep) goToStep(previousStep);
  };

  const skip = () => {
    onSkip?.();
  };

  const handleCreateAccount = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmitAccount || isAccountSubmitting) return;

    if (hasSubmittedAccount) {
      goToStep("microphone");
      return;
    }

    await onCreateAccount?.({
      ...values,
      email: values.email.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
    });
  };

  return (
    <main className="min-h-full bg-background text-foreground">
      <Wizard
        className="min-h-full max-w-[560px] justify-start px-6 pb-10 pt-[26px]"
        onValueChange={(value) => goToStep(value as SignUpStepId)}
        steps={[
          {
            value: "profile",
            title: "Tell us who you are",
            description:
              "We tailor the note, the codes, and how gaps surface to match your specialty.",
          },
          {
            value: "microphone",
            title: "Let's check your microphone",
            description:
              "The scribe listens during the visit. Set it up now so you're never interrupted mid-visit.",
          },
          {
            value: "preview",
            title: "See it run, start to finish",
            description: `Type, speak, or upload a gap list. ${PRODUCT_NAME.singular} builds the agenda, checks it off as you talk, and hands back the note and codes.`,
          },
          {
            value: "trust",
            title: "Your data, handled carefully",
            description: "Here's what happens to a visit, and what never does.",
          },
          {
            value: "verify-email",
            title: isInvitation
              ? "Accept your invitation"
              : "Verify your email",
            description: isInvitation
              ? "Confirm you're joining the practice that invited you."
              : "Use the link we send you to confirm this account belongs to you.",
          },
        ]}
        value={activeStep}
      >
        <WizardProgress
          className="mb-[66px]"
          complete={activeStep === "verify-email"}
        />

        <WizardPanel value="profile">
          {() => (
            <form
              aria-label={`Create ${PRODUCT_NAME.singular} account`}
              className="flex w-full flex-col items-center"
              onSubmit={handleCreateAccount}
            >
              <SignUpStepLayout
                description={
                  invitation
                    ? `Set up your account to join ${invitation.practiceName}.`
                    : "We tailor the note, the codes, and how gaps surface to match your specialty."
                }
                footer={
                  <SignUpStepFooter
                    ctaDisabled={
                      !canSubmitAccount ||
                      isAccountSubmitting ||
                      isLoadingInvitation ||
                      Boolean(invitationError)
                    }
                    ctaLabel="Continue"
                    ctaLoading={isAccountSubmitting}
                    hideBack
                    hideSkip
                    onSkip={skip}
                    submit
                  />
                }
                stepNumber={1}
                title="Tell us who you are"
                totalSteps={5}
              >
                <AccountProfileStep
                  error={invitationError ?? accountError}
                  isEmailLocked={isInvitation}
                  isSubmitting={isAccountSubmitting || isLoadingInvitation}
                  onChange={setValues}
                  values={values}
                />
              </SignUpStepLayout>
            </form>
          )}
        </WizardPanel>

        <WizardPanel value="microphone">
          {() => (
            <SignUpStepLayout
              description="The scribe listens during the visit. Set it up now so you're never interrupted mid-visit."
              footer={
                <SignUpStepFooter
                  ctaDisabled={!isMicrophoneReady}
                  ctaLabel="Continue"
                  onBack={goBack}
                  onContinue={() => goToStep("preview")}
                  onSecondary={() => goToStep("preview")}
                  onSkip={skip}
                  secondaryLabel={
                    isMicrophoneReady ? undefined : "I'll set this up later"
                  }
                />
              }
              stepNumber={2}
              title="Let's check your microphone"
              totalSteps={5}
            >
              <MicrophoneStep onReadyChange={setIsMicrophoneReady} />
            </SignUpStepLayout>
          )}
        </WizardPanel>

        <WizardPanel value="preview">
          {() => (
            <SignUpStepLayout
              description={`Type, speak, or upload a gap list. ${PRODUCT_NAME.singular} builds the agenda, checks it off as you talk, and hands back the note and codes.`}
              footer={
                <SignUpStepFooter
                  ctaLabel="Continue"
                  onBack={goBack}
                  onContinue={() => goToStep("trust")}
                  onSkip={skip}
                />
              }
              stepNumber={3}
              title="See it run, start to finish"
              totalSteps={5}
            >
              <LoopPreviewStep />
            </SignUpStepLayout>
          )}
        </WizardPanel>

        <WizardPanel value="trust">
          {() => (
            <ProvisioningGateStep
              ctaLabel={isInvitation ? "Accept invitation" : "Verify email"}
              provisioningView={provisioningView}
              onBack={goBack}
              onContinue={onComplete}
            />
          )}
        </WizardPanel>

        <WizardPanel value="verify-email">
          {() =>
            isInvitation ? (
              <SignUpStepLayout
                description="Confirm you're joining the practice that invited you."
                footer={
                  <SignUpStepFooter
                    ctaLabel={`Accept & enter ${PRODUCT_NAME.singular}`}
                    ctaLoading={isAcceptingInvitation}
                    hideSkip
                    onBack={goBack}
                    onContinue={onFinishInvitation}
                  />
                }
                stepNumber={5}
                title="Accept your invitation"
                totalSteps={5}
              >
                <InvitationJoinStep
                  email={displayedVerificationEmail}
                  error={invitationAcceptError}
                  practiceName={invitation?.practiceName}
                />
              </SignUpStepLayout>
            ) : (
              <SignUpStepLayout
                description="Use the link we send you to confirm this account belongs to you."
                footer={
                  <SignUpStepFooter
                    ctaLabel="I've verified my email"
                    ctaLoading={isEmailVerificationChecking}
                    hideSkip
                    onBack={goBack}
                    onContinue={onCheckEmailVerification}
                  />
                }
                stepNumber={5}
                title="Verify your email"
                totalSteps={5}
              >
                <EmailVerificationStep
                  email={displayedVerificationEmail}
                  error={emailVerificationError}
                  isChecking={isEmailVerificationChecking}
                  isResending={isVerificationEmailResending}
                  onResend={onResendVerificationEmail}
                />
              </SignUpStepLayout>
            )
          }
        </WizardPanel>

        <div
          aria-atomic="true"
          aria-live="polite"
          className="sr-only"
          role="status"
        >
          {"message" in provisioningView
            ? (provisioningView.message ?? "")
            : ""}
        </div>
      </Wizard>
    </main>
  );
}
